// prefsSync — local + cloud persistence for UI preferences.
//
// Extracted from uiStore.ts as the first slice of HI-2 (audit
// 2026-05-22). Before this split, ~275 LOC of localStorage / Supabase
// sync logic lived inline in uiStore alongside the actual store
// definition, making the file hard to navigate and tightly coupling
// the store's runtime shape to its persistence concerns.
//
// Responsibilities here:
//   - read/write the `fyrefly.ui.v1` localStorage blob
//   - read/write the legacy `fyrefly.instruments.v1` localStorage blob
//   - read/write `fyrefly.gloveMode.v1` + apply [data-glove] on <html>
//   - generic boolean-pref helpers (`fyrefly.<key>.v1`)
//   - debounced push of the UI prefs blob to Supabase `ui_prefs`
//   - one-shot pull from Supabase on sign-in, with merge-into-store
//
// The store itself (uiStore.ts) imports these helpers, calls them
// inside setters, and exposes `schedulePushUiPrefs` for the
// AccountSection re-sync button. No other consumer should reach in
// here directly.

import type { BaseImageryId, ChartOverlayId } from '../map/sources';
import type { LayerId } from '../map/layerCatalog';
import { setClock24hPref } from '../lib/timeFormat';
import {
  DEFAULT_INSTRUMENTS,
  INSTRUMENT_BY_ID,
  MAX_INSTRUMENTS,
  type InstrumentId,
} from '../lib/instruments';

type LayerVisibility = Record<LayerId, boolean>;

// ── Instrument-bar selection (legacy localStorage key) ───────────────

const INSTRUMENTS_LS_KEY = 'fyrefly.instruments.v1';

export function loadInstrumentSelection(): InstrumentId[] {
  if (typeof window === 'undefined' || !window.localStorage) return DEFAULT_INSTRUMENTS;
  try {
    const raw = window.localStorage.getItem(INSTRUMENTS_LS_KEY);
    if (!raw) return DEFAULT_INSTRUMENTS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_INSTRUMENTS;
    const valid = parsed
      .filter((id): id is InstrumentId => typeof id === 'string' && !!INSTRUMENT_BY_ID[id as InstrumentId] && INSTRUMENT_BY_ID[id as InstrumentId].available)
      .slice(0, MAX_INSTRUMENTS);
    return valid.length > 0 ? valid : DEFAULT_INSTRUMENTS;
  } catch {
    return DEFAULT_INSTRUMENTS;
  }
}

export function saveInstrumentSelection(ids: InstrumentId[]): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(INSTRUMENTS_LS_KEY, JSON.stringify(ids));
  } catch {
    // localStorage full / disabled — silently ignore. Selection still
    // works for the current session.
  }
}

// ── Glove mode ───────────────────────────────────────────────────────
// Persists to localStorage; applied to `<html>` via data-glove on every
// state change. Tailwind variants (defined in src/styles.css) target
// [data-glove="true"] to scale tap targets.

const GLOVE_LS_KEY = 'fyrefly.gloveMode.v1';

export function readGloveMode(): boolean {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    const raw = window.localStorage.getItem(GLOVE_LS_KEY);
    return raw === '1';
  } catch {
    return false;
  }
}

export function persistGloveMode(v: boolean): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(GLOVE_LS_KEY, v ? '1' : '0');
  } catch {}
}

export function applyGloveMode(v: boolean): void {
  if (typeof document === 'undefined') return;
  if (v) {
    document.documentElement.setAttribute('data-glove', 'true');
  } else {
    document.documentElement.removeAttribute('data-glove');
  }
}

// Apply persisted glove state on module load so the first paint
// already reflects the user's preference (no flicker).
if (typeof window !== 'undefined') {
  applyGloveMode(readGloveMode());
}

// ── Generic boolean-pref helpers ─────────────────────────────────────
// Used for the smaller flags added 2026-05-12 (keepScreenAwake,
// highContrastInstruments, etc.). Each gets its own
// `fyrefly.<key>.v1` localStorage slot.

export function readBoolPref(key: string, fallback: boolean): boolean {
  if (typeof window === 'undefined' || !window.localStorage) return fallback;
  try {
    const raw = window.localStorage.getItem(`fyrefly.${key}.v1`);
    if (raw == null) return fallback;
    return raw === '1';
  } catch {
    return fallback;
  }
}

export function persistBoolPref(key: string, v: boolean): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(`fyrefly.${key}.v1`, v ? '1' : '0');
  } catch {}
}

// ── UI preferences blob ──────────────────────────────────────────────
//
// One localStorage blob holds the user's persistent UI prefs so they
// stick across page reloads:
//   - railOpen (mobile rail expanded/collapsed)
//   - baseImagery / chartOverlay / chartOpacity (basemap stack)
//   - showInstruments (bottom instrument strip visibility)
//   - layers (visibility map for every overlay layer)
// Instrument selection has its own legacy key (above) and stays there
// for back-compat. Glove mode has its own key too. Everything else
// lives here.

const UI_PREFS_LS_KEY = 'fyrefly.ui.v1';

export interface PersistedUiPrefs {
  railOpen?: boolean;
  baseImagery?: BaseImageryId;
  chartOverlay?: ChartOverlayId;
  chartOpacity?: number;
  showInstruments?: boolean;
  /** Bottom instrument-bar selection — synced via ui_prefs so the
   *  user's pick survives logout / new browser / new device. The
   *  legacy localStorage key (fyrefly.instruments.v1) is still
   *  written for back-compat / same-device cache. */
  instruments?: InstrumentId[];
  layers?: LayerVisibility;
  trafficRangeNm?: number;
  trafficAltFilterFt?: number;
  trafficAutoDisableAboveFt?: number;
  trafficHideLowPriority?: boolean;
  /** Set to 1 once we've migrated old traffic defaults to the new
   *  off-by-default shape. See the one-shot migration in uiStore. */
  trafficPrefsMigrated?: number;
  trafficFilterOnlyAirborne?: boolean;
  showBreadcrumbs?: boolean;
  showRangeRings?: boolean;
  /** Day-1 spike 2026-05-16: 3D airspace + TFR volumes via fill-extrusion. */
  enable3d?: boolean;
  autoRecordTrackOnTakeoff?: boolean;
  autoStopTrackOnLanding?: boolean;
  clock24h?: boolean;
  /** Keep the device screen awake while flight tracking is active.
   *  Cloud-synced 2026-05-26 02:31 PT — boss reported display settings
   *  weren't carrying between sign-ins. */
  keepScreenAwake?: boolean;
  /** High-contrast instrument bar (white-on-black). Cloud-synced. */
  highContrastInstruments?: boolean;
  heliportsHospitalsOnly?: boolean;
  /** Terrain advisor settings — synced as a sub-key so terrain prefs
   *  persist across devices without a separate table. */
  terrain?: {
    presetId?: string;
    redFt?: number;
    yellowFt?: number;
    activationSpeedKt?: number;
    corridorMode?: string;
    corridorWidthNm?: number;
    alertsEnabled?: boolean;
  };
  /** Plan prefs — cruiseRule IFR/VFR persisted across devices. */
  plan?: {
    cruiseRule?: 'vfr' | 'ifr';
  };
  /** Glide-ring prefs — safety buffer above terrain at touchdown, in ft. */
  glideRing?: {
    safetyBufferFt?: number;
  };
  /** Radar variant pick (boss 2026-05-21 19:42 PDT). */
  nexradVariant?: 'classic' | 'composite' | 'lowtilt';
  /** HRRR-Smoke forecast offset (hours). Phase 1: 0/3/6/12/18.
   *  Synced 2026-05-26 so the boss's scrubber pick rides between
   *  devices the same way wind / nexrad selections do. */
  smokeOffsetH?: number;
  /** Wind-particles layer prefs (shipped 2026-05-20).
   *  altitudeFt: which level the slider is parked at (5K..34K)
   *  forecastHour: 0..24 (step 3) for the ±24h scrubber, snapped to GFS hours
   *  hideOnLowEnd: if true, skip rendering on devices we flag as low-end
   *                (set automatically in the layer mount on iPhone or
   *                memory<4GB; user can override via the layer-sheet detail).
   */
  wind?: {
    altitudeFt?: number;
    forecastHour?: number;
    hideOnLowEnd?: boolean;
    /** Auto-off when airborne. Defaults to true. Set false to override. */
    autoOffAirborne?: boolean;
  };
}

export function loadUiPrefs(): PersistedUiPrefs {
  if (typeof window === 'undefined' || !window.localStorage) return {};
  try {
    const raw = window.localStorage.getItem(UI_PREFS_LS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as PersistedUiPrefs;
  } catch {
    return {};
  }
}

export function persistUiPrefs(patch: PersistedUiPrefs): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const current = loadUiPrefs();
    const next = { ...current, ...patch };
    window.localStorage.setItem(UI_PREFS_LS_KEY, JSON.stringify(next));
    schedulePushUiPrefs();
  } catch {
    // Quota / disabled — silently degrade to session-only.
  }
}

// ── Supabase sync for UI prefs ───────────────────────────────────────
//
// Debounced upsert into app.ui_prefs (1:1 with profile). Single jsonb
// blob means new prefs sync automatically without schema migrations.
// The local localStorage write is still the source of truth; this
// layer ensures multiple devices stay in step when the user is
// signed in.

let uiPrefsPushTimer: ReturnType<typeof setTimeout> | null = null;
let authBootstrapped = false;

export function schedulePushUiPrefs(): void {
  if (typeof window === 'undefined') return;
  if (uiPrefsPushTimer) clearTimeout(uiPrefsPushTimer);
  uiPrefsPushTimer = setTimeout(() => {
    uiPrefsPushTimer = null;
    void pushUiPrefsToServer();
  }, 1500); // coalesce rapid changes (e.g. layer multi-toggle)
}

async function pushUiPrefsToServer(): Promise<void> {
  try {
    // Lazy-load to avoid an import cycle through authStore.
    // Also pull terrain + plan prefs from sibling stores so they
    // are always included in the server blob.
    const [{ supabase }, { useAuthStore }, terrainMod, planMod] = await Promise.all([
      import('../lib/supabase'),
      import('./authStore'),
      import('./terrainAdvisorStore'),
      import('./planStore'),
    ]);
    if (!supabase) return;
    const user = useAuthStore.getState().user;
    if (!user) return;
    const prefs: PersistedUiPrefs = {
      ...loadUiPrefs(),
      terrain: terrainMod.getTerrainPrefsForSync(),
      plan: { cruiseRule: planMod.usePlanStore.getState().cruiseRule },
    };
    const { error } = await supabase
      .from('ui_prefs')
      .upsert(
        { owner_id: user.id, prefs },
        { onConflict: 'owner_id' },
      );
    if (error) {
      // eslint-disable-next-line no-console
      console.warn('[prefsSync] ui_prefs push failed', error.message);
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[prefsSync] ui_prefs push threw', e);
  }
}

/** Pull authoritative prefs from server (called on sign-in). Server
 *  values overwrite local for keys present on the server; local-only
 *  keys are preserved. The store-apply callback is injected so this
 *  module doesn't import uiStore (avoids a circular dependency). */
export async function pullUiPrefsFromServer(
  applyToStore: (patch: Record<string, unknown>) => void,
): Promise<void> {
  try {
    const { supabase } = await import('../lib/supabase');
    const { useAuthStore } = await import('./authStore');
    if (!supabase) return;
    const user = useAuthStore.getState().user;
    if (!user) return;
    const { data, error } = await supabase
      .from('ui_prefs')
      .select('prefs')
      .eq('owner_id', user.id)
      .maybeSingle();
    if (error) {
      // eslint-disable-next-line no-console
      console.warn('[prefsSync] ui_prefs pull failed', error.message);
      return;
    }
    const remote = data?.prefs as PersistedUiPrefs | undefined;
    if (!remote) {
      // First sign-in on this user — push local up so other devices
      // see this device's preferences.
      await pushUiPrefsToServer();
      return;
    }
    // Merge: server wins on overlapping keys, then re-apply to the
    // running store so the UI reflects it immediately.
    const merged = { ...loadUiPrefs(), ...remote };
    if (typeof window !== 'undefined') {
      try { window.localStorage.setItem(UI_PREFS_LS_KEY, JSON.stringify(merged)); } catch {}
    }
    // Generic merge — any scalar key in PersistedUiPrefs that matches a
    // UiState key is applied automatically, so new prefs added to the
    // interface sync without touching this function.
    // Scalar keys that live in uiStore (traffic prefs moved to trafficPrefsStore — HI-2 step 2a;
    // instrument/display prefs moved to instrumentPrefsStore — HI-2 step 2b).
    const UI_SCALAR_KEYS = [
      'railOpen', 'baseImagery', 'chartOverlay', 'chartOpacity',
      'showBreadcrumbs', 'showRangeRings', 'autoRecordTrackOnTakeoff', 'autoStopTrackOnLanding',
      'heliportsHospitalsOnly',
      'enable3d',
    ] as const;
    const patch: Record<string, unknown> = {};
    for (const key of UI_SCALAR_KEYS) {
      const val = (merged as Record<string, unknown>)[key];
      if (val !== undefined && val !== null) patch[key] = val;
    }

    // Traffic prefs live in trafficPrefsStore (HI-2 step 2a). Apply
    // them lazily to avoid a circular import (trafficPrefsStore imports
    // prefsSync; prefsSync imports trafficPrefsStore here at call time).
    const TRAFFIC_SCALAR_KEYS = [
      'trafficRangeNm', 'trafficAltFilterFt', 'trafficAutoDisableAboveFt',
      'trafficHideLowPriority', 'trafficFilterOnlyAirborne',
    ] as const;
    const trafficPatch: Record<string, unknown> = {};
    for (const key of TRAFFIC_SCALAR_KEYS) {
      const val = (merged as Record<string, unknown>)[key];
      if (val !== undefined && val !== null) trafficPatch[key] = val;
    }
    if (Object.keys(trafficPatch).length > 0) {
      void import('./trafficPrefsStore').then(({ useTrafficPrefsStore }) => {
        useTrafficPrefsStore.setState(trafficPatch);
      });
    }
    applyToStore(patch);

    // Layer visibility lives in layerVisibilityStore (HI-2 step 2d).
    // Apply lazily to avoid a circular import (layerVisibilityStore imports
    // prefsSync; prefsSync imports layerVisibilityStore here at call time).
    if (merged.layers) {
      void import('./layerVisibilityStore').then(({ useLayerVisibilityStore, mergeWithDefaultLayers }) => {
        useLayerVisibilityStore.setState({ layers: mergeWithDefaultLayers(merged.layers) });
      });
    }

    // Instrument/display prefs live in instrumentPrefsStore (HI-2 step 2b).
    // Apply them lazily to avoid a circular import (instrumentPrefsStore
    // imports prefsSync; prefsSync imports instrumentPrefsStore here at
    // call time, so the dynamic import is required).
    const instrumentPatch: Record<string, unknown> = {};
    // showInstruments and clock24h are scalar keys in the cloud blob.
    if (merged.showInstruments !== undefined && merged.showInstruments !== null) {
      instrumentPatch.showInstruments = merged.showInstruments;
    }
    if (merged.clock24h !== undefined && merged.clock24h !== null) {
      instrumentPatch.clock24h = merged.clock24h;
    }
    // keepScreenAwake + highContrastInstruments — cloud-synced.
    // We also write back to the legacy per-key LS slots here so the
    // store's init hydration path stays consistent on the next reload.
    // The instrumentPrefsStore's setters mirror to the per-key slot,
    // but applying the patch via setState bypasses the setters, so the
    // per-key slot would otherwise go stale and the store would init
    // from the wrong source. Boss report 2026-05-26 13:23 PT.
    if (merged.keepScreenAwake !== undefined && merged.keepScreenAwake !== null) {
      instrumentPatch.keepScreenAwake = merged.keepScreenAwake;
      persistBoolPref('keepScreenAwake', merged.keepScreenAwake);
    }
    if (merged.highContrastInstruments !== undefined && merged.highContrastInstruments !== null) {
      instrumentPatch.highContrastInstruments = merged.highContrastInstruments;
      persistBoolPref('highContrastInstruments', merged.highContrastInstruments);
    }
    // Instrument bar selection — validate against known/available ids
    // before applying (server blob could be stale after we retire an
    // instrument). Also mirror to legacy LS so same-device reloads
    // before next sync see the right value.
    if (Array.isArray(merged.instruments)) {
      const validInstruments = (merged.instruments as InstrumentId[])
        .filter((id): id is InstrumentId =>
          typeof id === 'string'
          && !!INSTRUMENT_BY_ID[id as InstrumentId]
          && INSTRUMENT_BY_ID[id as InstrumentId].available,
        )
        .slice(0, MAX_INSTRUMENTS);
      if (validInstruments.length > 0) {
        instrumentPatch.instruments = validInstruments;
        saveInstrumentSelection(validInstruments);
      }
    }
    if (Object.keys(instrumentPatch).length > 0) {
      void import('./instrumentPrefsStore').then(({ useInstrumentPrefsStore }) => {
        // setState applies the patch fields directly (Zustand merges shallowly).
        useInstrumentPrefsStore.setState(instrumentPatch);
        // Keep timeFormat module state in sync when the server pull
        // overwrites the local clock24h value (cross-device migration).
        if (typeof instrumentPatch.clock24h === 'boolean') {
          setClock24hPref(instrumentPatch.clock24h);
        }
      });
    }
    // Wind prefs live in windPrefsStore (HI-2 step 2c). Apply them lazily
    // to avoid a circular import (windPrefsStore imports prefsSync;
    // prefsSync imports windPrefsStore here at call time, so the dynamic
    // import is required). Wind prefs persist as a nested `wind` sub-object
    // (NOT flat top-level keys) — we set each field individually so the
    // store's setters are NOT used here (they would re-persist to LS and
    // trigger another cloud push). We use setState directly like the
    // traffic patch above, which avoids the re-persist write.
    if (merged.wind) {
      const windPatch: Record<string, unknown> = {};
      if (merged.wind.altitudeFt !== undefined && merged.wind.altitudeFt !== null)
        windPatch.windAltitudeFt = merged.wind.altitudeFt;
      if (merged.wind.forecastHour !== undefined && merged.wind.forecastHour !== null)
        windPatch.windForecastHour = merged.wind.forecastHour;
      if (merged.wind.hideOnLowEnd !== undefined && merged.wind.hideOnLowEnd !== null)
        windPatch.windHideOnLowEnd = merged.wind.hideOnLowEnd;
      if (merged.wind.autoOffAirborne !== undefined && merged.wind.autoOffAirborne !== null)
        windPatch.windAutoOffAirborne = merged.wind.autoOffAirborne;
      if (Object.keys(windPatch).length > 0) {
        void import('./windPrefsStore').then(({ useWindPrefsStore }) => {
          useWindPrefsStore.setState(windPatch);
        });
      }
    }
    // Apply terrain prefs to terrainAdvisorStore (cross-device sync)
    if (merged.terrain) {
      void import('./terrainAdvisorStore').then(({ useTerrainAdvisorStore }) => {
        useTerrainAdvisorStore.getState().applyServerPrefs(merged.terrain!);
      });
    }
    // Apply plan prefs to planStore (cruise rule IFR/VFR)
    if (merged.plan?.cruiseRule) {
      void import('./planStore').then(({ usePlanStore }) => {
        usePlanStore.getState().setCruiseRule(merged.plan!.cruiseRule!);
      });
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[prefsSync] ui_prefs pull threw', e);
  }
}

/** Wire the auth-state subscription lazily so we don't create an
 *  import cycle when uiStore is hydrated before authStore. Caller
 *  supplies the setState callback so prefsSync can write back into
 *  the store without a hard dependency on uiStore (avoids a circular
 *  import). Layer visibility is applied directly to
 *  layerVisibilityStore inside pullUiPrefsFromServer (HI-2 step 2d). */
export function bootstrapAuthSyncWatcher(
  applyToStore: (patch: Record<string, unknown>) => void,
): void {
  if (typeof window === 'undefined') return;
  setTimeout(async () => {
    if (authBootstrapped) return;
    authBootstrapped = true;
    const { useAuthStore } = await import('./authStore');
    if (useAuthStore.getState().session) {
      void pullUiPrefsFromServer(applyToStore);
    }
    useAuthStore.subscribe((state, prev) => {
      if (!prev.user && state.user) {
        void pullUiPrefsFromServer(applyToStore);
      }
    });
  }, 0);
}
